import { useContext, useEffect, useRef, useState } from "react";
import { EnteredContext } from "../EnteredContext";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\<>*#";

function prefersReduced() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Small HUD eyebrow label with a three-stage "boot up" reveal, triggered once
 * when it scrolls into view (after the splash is dismissed):
 *   1. just the diamond            "◆"
 *   2. dashes load in like a bar   "◆ --------"
 *   3. dashes scramble into text   "◆ MISSION LOG"
 * Respects prefers-reduced-motion.
 *
 * Props: text (label without the diamond), as, className, diamond.
 */
export default function HudLabel({ text, as: Tag = "p", className = "", diamond = "◆" }) {
  const full = diamond + " " + text;
  // Start empty so the diamond appears as the first frame of the boot-up
  // (in sync with the rest of the section loading), not pre-rendered.
  const [display, setDisplay] = useState(() => (prefersReduced() ? full : ""));
  const ref = useRef(null);
  const startedRef = useRef(false);
  const ready = useContext(EnteredContext);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready) return undefined;

    if (prefersReduced()) {
      setDisplay(full);
      return undefined;
    }

    const L = text.length;
    const diamondHold = 140; // ms showing only the diamond
    const dashDur = 360; // ms for the dashes to fill in
    const decodeDur = 560; // ms for the scramble-to-text
    const total = diamondHold + dashDur + decodeDur;

    let raf;
    const run = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = now - start;
        let out;
        if (t < diamondHold) {
          out = diamond;
        } else if (t < diamondHold + dashDur) {
          const n = Math.floor(((t - diamondHold) / dashDur) * L);
          out = diamond + " " + "-".repeat(n);
        } else if (t < total) {
          const revealed = Math.floor(((t - diamondHold - dashDur) / decodeDur) * L);
          let s = "";
          for (let i = 0; i < L; i++) {
            if (i < revealed) s += text[i];
            else if (text[i] === " ") s += " ";
            else s += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          }
          out = diamond + " " + s;
        } else {
          out = full;
        }
        setDisplay(out);
        if (t < total) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !startedRef.current) {
          startedRef.current = true;
          run();
          io.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, diamond, full, ready]);

  return (
    <Tag ref={ref} className={className}>
      {display}
    </Tag>
  );
}
